/**
 * This is not a customization interface.
 * It is a factory function to create a utility function to generate the default getting available login method config function.
 */

// Types
import {
  AvailableLoginMethodsConfig,
  GetAvailableLoginMethodConfigParameter,
} from '@shopee-user/business-customization-types';
import { BiometricType } from '@tob/shared/biometrics/constants';

// Enum
import { AvailableLoginFlow } from '@tob/shared/login';

// Components
import {
  IconPassword,
  IconPhone,
  IconEmail,
  FaceIdIcon,
  FingerprintIcon,
  GenericBiometricIcon,
  PasscodeIcon,
  QuickLoginIcon,
} from '@tob/shared/login';

export function createGetAvailableLoginMethodConfigUtil() {
  function getAvailableLoginMethodConfig({
    biometricType,
    userInfo,
    isSupportBiometricsLogin,
    isSupportQuickLoginLogin,
    isSupportPasswordLogin,
    isSupportOtpLogin,
    currentLoginFlow,
    isLoginByBiometricsEmailOtpEnabled,
  }: GetAvailableLoginMethodConfigParameter): AvailableLoginMethodsConfig {
    return [
      ...getLoginByQuickLoginConfig({
        isSupportQuickLoginLogin,
      }),
      ...getLoginByBiometricsConfig({
        isSupportBiometricsLogin,
        biometricType,
      }),
      ...getLoginByPasswordConfig({
        isSupportPasswordLogin,
        userInfo,
        currentLoginFlow,
      }),
      ...getLoginByOtpConfig({
        isSupportOtpLogin,
        userInfo,
        currentLoginFlow,
        isLoginByBiometricsEmailOtpEnabled,
      }),
    ];
  }

  function getLoginByQuickLoginConfig({
    isSupportQuickLoginLogin,
  }: {
    isSupportQuickLoginLogin: boolean;
  }) {
    if (!isSupportQuickLoginLogin) {
      return [];
    } else {
      return [
        {
          label: 'label_auth_quick_login',
          flow: AvailableLoginFlow.LoginByQuickLogin,
          Icon: QuickLoginIcon,
        },
      ];
    }
  }

  function getLoginByPasswordConfig({
    userInfo,
    isSupportPasswordLogin,
    currentLoginFlow,
  }: {
    userInfo: GetAvailableLoginMethodConfigParameter['userInfo'];
    isSupportPasswordLogin: boolean;
    currentLoginFlow: GetAvailableLoginMethodConfigParameter['currentLoginFlow'];
  }) {
    const { username, email, phone } = userInfo || {};
    /**
     * * Login By Biometrics supports its own login by password flow
     * * Login By Password inside login by biometrics require either username, email, or phone to be available
     * * If these details are not provided, login by password will be disabled in login by biometrics
     * * Other login flows only depends on the feature toggle
     */
    const isLoginByPasswordEnabled =
      currentLoginFlow === AvailableLoginFlow.LoginByBiometrics
        ? isSupportPasswordLogin && (username || email || phone)
        : isSupportPasswordLogin;

    if (!isLoginByPasswordEnabled) {
      return [];
    }
    return [
      {
        label: 'label_auth_login_with_password',
        flow: AvailableLoginFlow.LoginByPassword,
        Icon: IconPassword,
      },
    ];
  }

  function getLoginByOtpConfig({
    userInfo,
    isSupportOtpLogin,
    currentLoginFlow,
    isLoginByBiometricsEmailOtpEnabled = false,
  }: {
    userInfo: GetAvailableLoginMethodConfigParameter['userInfo'];
    isSupportOtpLogin: boolean;
    currentLoginFlow: GetAvailableLoginMethodConfigParameter['currentLoginFlow'];
    isLoginByBiometricsEmailOtpEnabled?: boolean;
  }) {
    /**
     * * Login By Biometrics supports its own login by otp flow
     * * Login By Otp inside login by biometrics require either username, email, or phone to be available
     * * If these details are not provided, login by otp will be disabled in login by biometrics
     * * Other login flows only depends on the feature toggle
     */
    /** Added a new LoginByBiometricsEmailOtp toggle due to:
     * Login By Biometrics will separate Login By Phone and Login by Email flows
     * Login By OTP will not separate these flows (shared flow for both Phone and Email credential)
     */
    const { email, phone } = userInfo || {};
    const isLoginByOtpEnabled =
      currentLoginFlow === AvailableLoginFlow.LoginByBiometrics
        ? isSupportOtpLogin &&
          (phone || (isLoginByBiometricsEmailOtpEnabled && email))
        : isSupportOtpLogin;

    if (!isLoginByOtpEnabled) {
      return [];
    }

    return [
      ...(phone
        ? [
            {
              label: 'label_auth_login_with_phone',
              flow: AvailableLoginFlow.LoginByOtp,
              Icon: IconPhone,
            },
          ]
        : []),
      ...(isLoginByBiometricsEmailOtpEnabled && email
        ? [
            {
              label: 'label_auth_login_with_email',
              flow: AvailableLoginFlow.LoginByEmailOtp,
              Icon: IconEmail,
            },
          ]
        : []),
    ];
  }

  function getLoginByBiometricsConfig({
    biometricType,
    isSupportBiometricsLogin,
  }: {
    biometricType?: BiometricType;
    isSupportBiometricsLogin: boolean;
  }) {
    const isBiometricLoginEnabled = isSupportBiometricsLogin;
    if (!isBiometricLoginEnabled || biometricType === undefined) {
      return [];
    }

    return [
      ...(biometricType === BiometricType.FaceId
        ? [
            {
              label: 'label_auth_login_with_face_id',
              flow: AvailableLoginFlow.LoginByBiometrics,
              Icon: FaceIdIcon,
            },
          ]
        : []),
      ...(biometricType === BiometricType.TouchId
        ? [
            {
              label: 'label_auth_login_with_touch_id',
              flow: AvailableLoginFlow.LoginByBiometrics,
              Icon: FingerprintIcon,
            },
          ]
        : []),
      ...(biometricType === BiometricType.Unknown
        ? [
            {
              label: 'label_auth_login_with_biometrics',
              flow: AvailableLoginFlow.LoginByBiometrics,
              Icon: GenericBiometricIcon,
            },
          ]
        : []),
      ...(biometricType === BiometricType.Fingerprint
        ? [
            {
              label: 'label_auth_login_with_biometrics',
              flow: AvailableLoginFlow.LoginByBiometrics,
              Icon: GenericBiometricIcon,
            },
          ]
        : []),
      ...(biometricType === BiometricType.LockScreenPasscode
        ? [
            {
              label: 'label_auth_login_with_passcode',
              flow: AvailableLoginFlow.LoginByBiometrics,
              Icon: PasscodeIcon,
            },
          ]
        : []),
    ];
  }

  return {
    getAvailableLoginMethodConfig,
  };
}
